import { useState, FormEvent } from 'react';

export default function Hero() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (formData.website) {
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);

      const response = await fetch('https://readdy.ai/api/form/d5rke0498t1oshnre940', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', website: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section
      id="hero"
      className="min-h-screen bg-gradient-to-b from-[#003366] to-[#4A90E2] pt-20 md:pt-24 flex flex-col justify-center relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-[#FFD700]/10 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-[#FFD700]/10 rounded-full filter blur-3xl"></div>

      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="text-left">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
              ECONOMIZE ATÉ <span className="text-[#FFD700]">30%</span> NA SUA CONTA DE ENERGIA
            </h1>
            <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8">
              Descubra como empresas que gastam mais de R$10.000 por mês em energia estão reduzindo
              seus custos operacionais.
            </p>
            <div className="bg-white/10 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-xl border border-white/20">
              <p className="text-[#FFD700] font-semibold text-base md:text-lg">
                Solicite agora sua análise de viabilidade GRATUITA e descubra quanto sua empresa
                pode economizar.
              </p>
            </div>
          </div>

          <div className="w-full">
            <div className="bg-white/10 backdrop-blur-lg p-6 md:p-8 rounded-xl shadow-2xl border border-white/20">
              <h2 className="text-xl md:text-3xl font-bold text-white mb-3 md:mb-4 text-center">
                SOLICITE SUA ANÁLISE GRATUITA
              </h2>
              <p className="text-center text-white/90 mb-4 md:mb-6 text-sm md:text-base">
                Preencha o formulário e descubra quanto você pode economizar
              </p>

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5" data-readdy-form>
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-white font-medium text-sm">
                    Nome do responsável:
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full h-12 rounded-lg border border-white/30 bg-white/10 text-white placeholder-white/70 px-4 py-2 focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700] focus:outline-none text-sm"
                    placeholder="Seu nome completo"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-white font-medium text-sm">
                    E-mail:
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full h-12 rounded-lg border border-white/30 bg-white/10 text-white placeholder-white/70 px-4 py-2 focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700] focus:outline-none text-sm"
                    placeholder="seu.email@empresa.com.br"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-white font-medium text-sm">
                    Telefone:
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full h-12 rounded-lg border border-white/30 bg-white/10 text-white placeholder-white/70 px-4 py-2 focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700] focus:outline-none text-sm"
                    placeholder="(00) 00000-0000"
                    required
                  />
                </div>

                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <label htmlFor="website">Website (não preencha):</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 md:py-4 px-6 rounded-lg font-bold text-sm md:text-base uppercase tracking-wider transition-all duration-300 flex items-center justify-center bg-[#FFD700] hover:bg-yellow-400 text-[#003366] shadow-md hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR ANÁLISE AGORA'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-white p-4 rounded-lg text-center text-sm">
                    Obrigado! Entraremos em contato em breve.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-white p-4 rounded-lg text-center text-sm">
                    Erro ao enviar. Tente novamente.
                  </div>
                )}

                <p className="text-xs text-white/70 text-center">
                  Seus dados estão protegidos conforme a LGPD
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer animate-bounce">
        <i className="ri-arrow-down-line text-white text-2xl md:text-3xl w-8 h-8 flex items-center justify-center"></i>
      </div>
    </section>
  );
}
